import { z } from 'zod';

/**
 * Deep link registry of an environment (BB-127, DL-001…DL-004): the app routes a message may open, with their parameters,
 * who may open them (`auth`) and on which platforms they work. Builders and validators of message actions use it.
 */
export const DEEPLINK_PARAM_TYPES = ['string', 'int', 'enum', 'bool'] as const;
export const DEEPLINK_AUTH = ['any', 'authorized', 'unauthorized'] as const;

export const DeeplinkParam = z
  .strictObject({
    name: z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/),
    type: z.enum(DEEPLINK_PARAM_TYPES),
    enumValues: z.array(z.string().min(1).max(100)).max(50).optional(),
    required: z.boolean().default(false),
    example: z.string().max(500).optional(),
  })
  .refine((p) => (p.type === 'enum') === Boolean(p.enumValues?.length), { message: 'enum params list their values, other types none', path: ['enumValues'] });
export type DeeplinkParam = z.infer<typeof DeeplinkParam>;

const Platform = z.strictObject({ minVersion: z.string().regex(/^\d+(\.\d+){0,3}$/).optional() });
export const DeeplinkPlatforms = z
  .strictObject({ android: Platform.optional(), ios: Platform.optional() })
  .refine((p) => Boolean(p.android || p.ios), 'a route works on at least one platform');

const routeFields = {
  title: z.string().trim().min(1).max(120),
  description: z.string().trim().max(1000).default(''),
  owner: z.string().trim().max(80).default('mobile'),
  params: z.array(DeeplinkParam).max(30).default([]),
  auth: z.enum(DEEPLINK_AUTH).default('any'),
  platforms: DeeplinkPlatforms,
  deprecated: z.boolean().default(false),
};

export const CreateDeeplinkBody = z
  .strictObject({ scheme: z.string().regex(/^[a-z][a-z0-9+.-]{0,31}$/), host: z.string().regex(/^[a-z0-9_-]{1,64}$/), ...routeFields })
  .refine((b) => new Set(b.params.map((p) => p.name)).size === b.params.length, { message: 'param names repeat', path: ['params'] });
export type CreateDeeplinkBody = z.infer<typeof CreateDeeplinkBody>;

export const UpdateDeeplinkBody = z.strictObject({
  title: routeFields.title.optional(),
  description: z.string().trim().max(1000).optional(),
  owner: z.string().trim().max(80).optional(),
  params: z.array(DeeplinkParam).max(30).optional(),
  auth: z.enum(DEEPLINK_AUTH).optional(),
  platforms: DeeplinkPlatforms.optional(),
  deprecated: z.boolean().optional(),
});
export type UpdateDeeplinkBody = z.infer<typeof UpdateDeeplinkBody>;

export const DeeplinkListQuery = z.object({
  query: z.string().trim().max(200).optional(),
  platform: z.enum(['android', 'ios', 'both']).optional(),
  includeDeprecated: z.enum(['true', 'false']).default('false'),
});
export const ValidateDeeplinkQuery = z.object({ uri: z.string().min(1).max(2048), platform: z.enum(['android', 'ios']).optional() });
export const BuildDeeplinkBody = z.strictObject({ routeId: z.uuid(), params: z.record(z.string(), z.string().max(2048)).default({}) });

export interface DeeplinkRouteView extends Omit<CreateDeeplinkBody, never> {
  id: string;
  updatedAt: string;
}

export interface DeeplinkIssue {
  code: 'not_a_uri' | 'unknown_scheme' | 'unknown_host' | 'missing_param' | 'invalid_param' | 'unknown_param' | 'deprecated_route' | 'platform_missing';
  message: string;
  severity: 'error' | 'warning';
  param?: string;
}

export interface DeeplinkValidation {
  ok: boolean;
  /** `web` for http(s) links (they open in a browser, not through the registry). */
  kind: 'deeplink' | 'web';
  routeId: string | null;
  issues: DeeplinkIssue[];
}
